// ./pages/[...path].js
import querystring from 'querystring';
import {
  getPage,
  getRedirect,
  getLocales,
  WagtailApiResponseError
} from '../api/wagtail';
import { getNavigation } from '../api/navigation';
import { getFooterPages } from '../api/footer';
import { getTaxonomy } from '../api/taxonomy';
import { PageContextProvider } from '../api/PageContext';
import dynamic from 'next/dynamic';
import LazyContainers from '../containers/LazyContainers';
import { AppShell } from '../components/Templates';
import BasePage from '../containers/BasePage/BasePage';

const isProd = process.env.NODE_ENV === 'production';

export default function CatchAllPage({ componentName, componentProps, shellProps }) {
    const importFn = LazyContainers[componentName];

    if (!importFn) {
        return <h1>Component {componentName} not found</h1>;
    }

    const Component = dynamic(importFn);

    return (
        <AppShell
            componentProps={componentProps}
            primaryPages={shellProps?.primaryPages || []}
            locales={shellProps?.locales || []}
            footerPages={shellProps?.footerPages || { columns: [], copyright: '' }}
            taxonomyGroups={shellProps?.taxonomyGroups || []}
        >
            <BasePage
                seo={componentProps?.seo}
                wagtailUserbar={componentProps?.wagtailUserbar}
            >
                <Component {...componentProps} />
            </BasePage>
        </AppShell>
    );
}

export async function getStaticProps({ params }) {
  let pathArray = params?.path || [];
  let path = pathArray.length === 0 ? '/' : pathArray.join('/');

  // Detect locale from path — /en/... → 'en', /fr/... → 'fr'
  const localeCode = pathArray[0]?.length === 2 ? pathArray[0] : 'en';

  try {
    const [
      pageResult,
      navData,
      footerData,
      localesData,
      taxonomyGroups,
    ] = await Promise.all([
      getPage(path, {}, {}),
      getNavigation(localeCode).catch(() => ({ primaryPages: [], navGroupMap: {} })),
      getFooterPages(localeCode).catch(() => ({ columns: [], copyright: '' })),
      getLocales().catch(() => []),
      getTaxonomy().catch(() => []),
    ]);
    const { componentName, componentProps, redirect } = pageResult.json;

    // Keep backend redirect handling first
    if (redirect) {
      return {
        redirect: {
          destination: redirect.destination,
          permanent: redirect.isPermanent
        },
        revalidate: 300
      };
    }

    // Normalize current path to match navigation URLs like /en/foo/bar/
    const normalizedCurrentUrl =
      path === '/' ? '/' : `/${path.replace(/^\/|\/$/g, '')}/`;

    const navGroupDestination = navData?.navGroupMap?.[normalizedCurrentUrl];

    if (navGroupDestination && navGroupDestination !== normalizedCurrentUrl) {
      return {
        redirect: {
          destination: navGroupDestination,
          permanent: false,
        },
        revalidate: 300,
      };
    }

    return {
      props: {
        componentName,
        componentProps,
        layoutConfig: componentProps?.layoutConfig || null,
        shellProps: {
          primaryPages: navData?.primaryPages || [],
          locales: localesData || [],
          footerPages: footerData || { columns: [], copyright: '' },
          taxonomyGroups: taxonomyGroups || [],
        },
      },
      revalidate: 300
    };

  } catch (err) {
    if (!(err instanceof WagtailApiResponseError)) {
      console.error('Error fetching page:', err);
      throw err;
    }

    return {
      notFound: true,
      revalidate: 300
    };
  }
}

export async function getStaticPaths() {
  return {
    paths: [],
    fallback: 'blocking',
  };
}

/*
export async function getServerSideProps({ req, params, res }) {
  console.log('=== DEBUG INFO ===');
  console.log('params:', params);
  console.log('req.url:', req.url);
  console.log('req.headers.host:', req.headers.host);
  console.log('==================');
  
  let path = params?.path || [];
  path = path.join('/');

  const { host } = req.headers;
  let queryParams = new URL(req.url, `https://${host}`).search;
  if (queryParams.indexOf('?') === 0) {
    queryParams = queryParams.substr(1);
  }
  queryParams = querystring.parse(queryParams);

  let primaryPages = [];
  let locales = [];

  try {
    primaryPages = await getPrimaryPages();

    for (const primaryPage of primaryPages) {
      if (primaryPage.url) {
        try {
          const secondaryPages = await getSecondaryPages(primaryPage.url);
          primaryPage.secondaryPages = secondaryPages;
        } catch (err) {
          console.error(
            `Error fetching secondary pages for ${primaryPage.url}:`,
            err
          );
        }
      }
    }
  } catch (err) {
    console.error('Error fetching primary pages:', err);
  }

  try {
    locales = await getLocales();
  } catch (err) {
    console.error('Error fetching locales:', err);
  }

  try {
    const {
      json: { componentName, componentProps, redirect, customResponse },
      headers
    } = await getPage(path, queryParams, {
      headers: {
        cookie: req.headers.cookie,
        host
      }
    });

    const cookies = headers.get('set-cookie');
    if (cookies) {
      res.setHeader('Set-Cookie', cookies);
    }

    if (customResponse) {
      const { body, body64, contentType } = customResponse;
      res.setHeader('Content-Type', contentType);
      res.statusCode = 200;
      res.write(body64 ? Buffer.from(body64, 'base64') : body);
      res.end();
      return { props: {} };
    }

    if (redirect) {
      const { destination, isPermanent } = redirect;
      return {
        redirect: {
          destination,
          permanent: isPermanent
        }
      };
    }

    return {
      props: {
        componentName,
        componentProps,
        primaryPages,
        locales
      }
    };
  } catch (err) {
    if (!(err instanceof WagtailApiResponseError)) {
      console.error('Error fetching page:', err);
      throw err;
    }
    return { props: {} };
  }
}
*/

// For SSG
/*
export async function getStaticProps({ params, preview, previewData }) {
    params = params || {};
    let path = params.path || [];
    path = path.join("/");

    const { json: pageData } = await getPage(path);
    return { props: pageData }
}

export async function getStaticPaths() {
    const { json: data } = await getAllPages();

    let htmlUrls = data.items.map(x => x.relativeUrl);
    htmlUrls = htmlUrls.filter(x => x);
    htmlUrls = htmlUrls.map(x => x.split("/"));
    htmlUrls = htmlUrls.map(x => x.filter(y => y))
    htmlUrls = htmlUrls.filter(x => x.length)

    const paths = htmlUrls.map(x => (
        { params: { path: x } }
    ));

    return {
        paths: paths,
        fallback: false,
    };
}
*/